import { BaseDialog } from '@gc-bridge/components/Dialog/Base';
import { BuildCircleOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { noop } from 'lodash';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface MaintenanceModeContextValue {
  openMaintenanceDialog: () => void;
  closeMaintenanceDialog: () => void;
}

const defaultContextValue: MaintenanceModeContextValue = {
  openMaintenanceDialog: noop,
  closeMaintenanceDialog: noop,
};

const MaintenanceModeContext =
  createContext<MaintenanceModeContextValue>(defaultContextValue);

interface Props {
  children: ReactNode;
}

export function MaintenanceModeProvider({ children }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const openMaintenanceDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeMaintenanceDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const dialog = useMemo(() => {
    return (
      <BaseDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="Maintenance Mode"
        subtitle={
          <>
            The application is temporarily unavailable due to scheduled maintenance.
            <br />
            <br />
            You can wait here and we will automatically restore your work when maintenance
            is complete, or you can return to <strong>GC Bridge</strong>.
          </>
        }
        dialogActions={
          <Button
            variant="contained"
            onClick={() => {
              closeMaintenanceDialog();
              window.location.href = '/';
            }}
          >
            Back to Bridge
          </Button>
        }
        leftAdornment={
          <BuildCircleOutlined color="primary" sx={{ width: '30px', height: '30px' }} />
        }
      />
    );
  }, [closeMaintenanceDialog, isDialogOpen]);

  return (
    <MaintenanceModeContext.Provider
      value={{
        openMaintenanceDialog,
        closeMaintenanceDialog,
      }}
    >
      {children}
      {dialog}
    </MaintenanceModeContext.Provider>
  );
}

export const useMaintenanceMode = () => useContext(MaintenanceModeContext);
